import React from 'react';
import { A11y, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Heading from '@ff/ui/Heading';
import Icon from '@ff/ui/Icon';
import Image from '@ff/ui/Image';
import Label from '@ff/ui/Label';
import Link from '@ff/ui/Link';
import Paragraph from '@ff/ui/Paragraph';
import emptyCircleLight from 'shared/assets/slick-dots/empty-circle-light.svg';
import emptyCircleDark from 'shared/assets/slick-dots/empty-circle-dark.svg';
import filledCircleLight from 'shared/assets/slick-dots/filled-circle-light.svg';
import filledCircleDark from 'shared/assets/slick-dots/filled-circle-dark.svg';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/pagination';

type ColorTheme = 'LIGHT' | 'DARK';

type Props = {
  items: {
    image: string;
    title: string;
    description: string;
    link: {
      label: string;
      url: string;
    };
  }[];
  title?: string;
  colorTheme?: ColorTheme;
};

type Widget<P> = React.FC<P> & { schema?: unknown };

const Block = ({
  description,
  hasEvenElementCount = false,
  image,
  link,
  title,
}) => (
  <Container hasEvenElementCount={hasEvenElementCount}>
    <ImageBlock src={image} alt={title || 'Faraday Future'} />
    <TitleLabel query={{ _: 'H5', sm: 'H4' }}>
      {title}
    </TitleLabel>
    <Description query={{ _: 'P3', sm: 'P1' }}>
      {description}
    </Description>
    <LinkContent>
      {(link.url && link.label) && (
        <StyledLink href={link.url} display='flex'>
          <LinkLabel>{link.label}</LinkLabel>
          <Icon name='ff-icon-arrow-paginate-next' fontSize='14px'/>
        </StyledLink>
      )}
    </LinkContent>
  </Container>
);

const Container = styled(Box)<{ hasEvenElementCount: boolean }>(
  ({ theme, hasEvenElementCount }) => ({
    display: 'flex',
    flexDirection: 'column',
    [theme.mediaQueries.sm]: {
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
    },
    ':nth-of-type(even)': hasEvenElementCount && {
      borderTop: 'none',
      [theme.mediaQueries.md]: {
        // Show border when there is no wrapping
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
      },
    },
  })
);

const ImageBlock = styled(Image)(({ theme }) => ({
  height: '400px',
  marginTop: '25px',
  objectFit: 'cover',
  objectPosition: 'center',
  width: '100%',
  [theme.mediaQueries.md]: {
    marginTop: '35px'
  }
}));

const TitleLabel = styled(Heading.Responsive)({
  paddingBottom: '15px',
  paddingTop: '30px',
});

const Description = styled(Paragraph.Responsive)(({ theme }) => ({
  flex: 1,
  minHeight: '100px',
  [theme.mediaQueries.xs]: {
    minHeight: '60px',
  },
  [theme.mediaQueries.sm]: {
    minHeight: '120px',
  },
  [theme.mediaQueries.md]: {
    minHeight: '140px',
  }
}));

const StyledLink = styled(Link)(({ theme }) => ({
  bottom: '30px',
  position: 'absolute',
  [theme.mediaQueries.sm]: {
    bottom: 'auto',
    position: 'relative',
  },
}));

const LinkContent = styled(Box)(({ theme }) => ({
  flex: 'none',
  marginTop: '25px',
  minHeight: '45px',
  [theme.mediaQueries.sm]: {
    marginBottom: '7px',
    marginTop: '60px',
  },
  [theme.mediaQueries.md]: {
    marginTop: '35px',
  },
}));

const LinkLabel = styled(Label.L4)({
  display: 'inline-block',
  paddingRight: '10px',
});

const FeatureBlocks: Widget<Props> = (props) => {
  const { colorTheme, items, title } = props;
  const hasEvenElementCount = items.length % 2 === 0;

  return (
    <FeatureBlocksContainer colorTheme={colorTheme}>
      {title && (
        <Heading.Responsive query={{ _: 'H3', sm: 'H1' }} textAlign='center'>
          {title}
        </Heading.Responsive>
      )}
      <MinMargin>
        <DesktopGrid count={items.length}>
          {items.map(({ description, image, link, title }) => (
            <Block
              link={link}
              image={image}
              key={title}
              hasEvenElementCount={hasEvenElementCount}
              title={title}
              description={description}
            />
          ))}
        </DesktopGrid>
      </MinMargin>
      <MobileSlider
        color={colorTheme}
        loop={true}
        modules={[Pagination, A11y]}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        pagination={{ clickable: true }}
        spaceBetween={30}
      >
        {items.map(({ description, image, link, title }) => (
          <Slide key={title}>
            <Block
              description={description}
              image={image}
              link={link}
              title={title}
            />
          </Slide>
        ))}
      </MobileSlider>
    </FeatureBlocksContainer>
  );
};

export default FeatureBlocks;

const FeatureBlocksContainer = styled(Box)<{ colorTheme: ColorTheme }>(({ theme, colorTheme }) => ({
  backgroundColor: colorTheme === 'DARK' ? 'black' : 'white',
  borderColor: colorTheme === 'DARK' ? 'white' : 'black',
  color: colorTheme === 'DARK' ? 'white' : 'black',
  paddingBottom: '10px',
  paddingTop: '25px',
  position: 'relative',
  paddingLeft: '15px',
  paddingRight: '15px',
  [theme.mediaQueries.sm]: {
    paddingLeft: 0,
    paddingRight: 0,
  }
}));

const DesktopGrid = styled.Box<{ count: number }>(({ theme, count }) => ({
  gridTemplateColumns: `repeat(${count}, 1fr)`,
  gridTemplateRows: 'repeat(2, auto)',
  gridAutoFlow: 'column',
  columnGap: '30px',

  '& > *': {
    // Limit max width based on item count
    maxWidth: `calc(${theme.breakpoints.xl} / ${count})`,
  },

  // Only shown on desktop
  display: 'none',
  [theme.mediaQueries.sm]: {
    display: 'grid',
    gridAutoFlow: 'dense',
    margin: '0 auto', // Center columns
    maxWidth: `calc(${theme.breakpoints.xl})`,
  },

  /* Wrap blocks for grid with even numbers of elements only */

  // Container of left column in two column arrangement
  '& > *:nth-of-type(4n + 1), & > *:nth-of-type(4n + 2)': count % 2 === 0 &&
    count > 2 && {
      gridColumnStart: 1,
      gridColumnEnd: 3,
      [theme.mediaQueries.md]: {
        gridColumnStart: 'unset',
        gridColumnEnd: 'unset',
      },
    },
  // Container of right column in two column arrangement
  '& > *:nth-of-type(4n + 3), & > *:nth-of-type(4n)': count % 2 === 0 &&
    count > 2 && {
      gridColumnStart: 3,
      gridColumnEnd: 5,
      [theme.mediaQueries.md]: {
        gridColumnStart: 'unset',
        gridColumnEnd: 'unset',
      },
    },
}));

const MobileSlider = styled(Swiper)<{ color: ColorTheme }>(({ theme, color }) => ({
  marginBottom: '10px',
  // Swiper styles
  '& .swiper-slide': {
    height: 'auto', // keep all the slide heights the same
  },
  '& .swiper-pagination': {
    marginTop: '17px',
    position: 'static',
  },
  '& .swiper-pagination-bullet': {
    background: 'none',
    border: 'none',
    content: `url("${color === 'DARK' ? emptyCircleDark : emptyCircleLight}")`,
    height: '12px',
    margin: '2px !important',
    opacity: 1,
    width: '12px',
  },
  '& .swiper-pagination-bullet-active': {
    background: 'none',
    border: 'none',
    color: 'black',
    content: `url("${color === 'DARK' ? filledCircleDark : filledCircleLight}")`,
    height: '12px',
    opacity: 1,
    width: '12px',
  },
  '& .swiper-button-prev': {
    '&:after': {
      color: 'black',
      fontSize: '16px',
    },
  },
  '& .swiper-button-next': {
    '&:after': {
      color: 'black',
      fontSize: '16px',
    },
  },
  // Hide on non-mobile devices
  [theme.mediaQueries.sm]: {
    display: 'none',
  },
}));

const Slide = styled(SwiperSlide)({
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
});

const MinMargin = styled.Box(({ theme }) => ({
  margin: '20px 36px',
  [theme.mediaQueries.md]: {
    margin: '30px 36px',
  }
}));
